// src/components/TestimonialsSection.jsx
import React, { useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Avatar,
  IconButton,
  useTheme
} from '@mui/material';
import { Star, ArrowBackIosNew, ArrowForwardIos } from '@mui/icons-material';
import { motion } from 'framer-motion';

const totalReviews = 9; // e.g. “100% recommend (9 Reviews)”

const reviews = [
  {
    name: 'Praba Holisman',
    avatar: '/avatars/praba.jpg',
    comment: 'They understand your vision & make it reality 😍 Thank you so much for your hard work on my website! ❤️',
    date: '3y'
  },
  {
    name: 'Traditional Foodking',
    avatar: '/avatars/foodking.jpg',
    comment: 'Fast & quality design work. Crafting unique business posts that stand out.',
    date: '3y'
  },
  {
    name: 'Tecno Ravi',
    avatar: '/avatars/ravi.jpg',
    comment: 'This is the most advantageous institution I have ever seen. LEMDA ✅ After-service, stunning UI and great value.',
    date: '3y'
  },
  {
    name: 'Dílsháñ Mìñdíkã',
    avatar: '/avatars/mindika.jpg',
    comment: 'Perfect work! ❤✨ I’m really satisfied—5 stars! Very friendly service 🔥 Highly recommend ❤',
    date: '3y'
  },
  // …add your other 5 here to reach 9 total
];

export default function TestimonialsSection() {
  const theme = useTheme();
  const carouselRef = useRef();

  const scroll = (direction) => {
    const node = carouselRef.current;
    if (!node) return;
    const scrollAmount = node.clientWidth * 0.8;
    node.scrollBy({ 
      left: direction === 'left' ? -scrollAmount : scrollAmount, 
      behavior: 'smooth' 
    });
  };

  return (
    <Box
      id="feedback"
      sx={{
        bgcolor: theme.palette.background.paper,
        py: { xs: 6, md: 10 }
      }}
    >
      <Container>
        <Typography
          variant="h4"
          align="center"
          fontWeight={700}
          gutterBottom
        >
          What Our Clients Say
        </Typography>

        {/* Summary */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            bgcolor: theme.palette.primary.light,
            color: theme.palette.primary.contrastText,
            px: 3,
            py: 1,
            borderRadius: 2,
            mx: 'auto',
            mb: 4
          }}
        >
          {[...Array(5)].map((_, i) => (
            <Star key={i} sx={{ fontSize: 24 }} />
          ))}
          <Typography variant="subtitle1" sx={{ ml: 1 }}>
            100% recommend ({totalReviews} Reviews)
          </Typography>
        </Box>

        {/* Carousel with arrows */}
        <Box position="relative">
          <IconButton
            onClick={() => scroll('left')}
            sx={{
              position: 'absolute',
              left: -16,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: theme.palette.background.paper,
              boxShadow: 2,
              '&:hover': { bgcolor: theme.palette.background.paper },
              zIndex: 2
            }}
          >
            <ArrowBackIosNew />
          </IconButton>

          <Box
            ref={carouselRef}
            component={motion.div}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{
              hidden: { opacity: 0 },
              visible: { opacity: 1, transition: { staggerChildren: 0.2 } }
            }}
            sx={{
              display: 'flex',
              overflowX: 'auto',
              scrollBehavior: 'smooth',
              gap: 3,
              px: 2,
              '&::-webkit-scrollbar': { display: 'none' }
            }}
          >
            {reviews.map((r, i) => (
              <motion.div
                key={i}
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0 }
                }}
              >
                <Box
                  component="div"
                  sx={{
                    position: 'relative',
                    minWidth: 300,
                    maxWidth: 300,
                    bgcolor: theme.palette.grey[50],
                    borderRadius: 3,
                    boxShadow: 3,
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      boxShadow: 8,
                      transform: 'translateY(-8px)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  {/* Accent circle */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -12,
                      right: -12,
                      width: 24,
                      height: 24,
                      bgcolor: theme.palette.secondary.main,
                      borderRadius: '50%'
                    }}
                  />

                  {/* Header */}
                  <Box display="flex" alignItems="center" mb={2}>
                    <Avatar
                      src={r.avatar}
                      sx={{ width: 48, height: 48, mr: 2 }}
                    />
                    <Box>
                      <Typography fontWeight={600}>{r.name}</Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        {r.date} ago
                      </Typography>
                    </Box>
                  </Box>

                  {/* Stars */}
                  <Box mb={1}>
                    {[...Array(5)].map((_, si) => (
                      <Star
                        key={si}
                        sx={{ 
                          color: theme.palette.secondary.main, 
                          fontSize: 18, 
                          mr: 0.5 
                        }}
                      />
                    ))}
                  </Box>

                  {/* Comment */}
                  <Typography variant="body2" sx={{ flexGrow: 1 }}>
                    “{r.comment}”
                  </Typography>
                </Box>
              </motion.div>
            ))}
          </Box>

          <IconButton
            onClick={() => scroll('right')}
            sx={{
              position: 'absolute',
              right: -16,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: theme.palette.background.paper,
              boxShadow: 2,
              '&:hover': { bgcolor: theme.palette.background.paper },
              zIndex: 2
            }}
          >
            <ArrowForwardIos />
          </IconButton>
        </Box>
      </Container>
    </Box>
  );
}
